import { z } from 'zod';

// ─── Ticket Type ─────────────────────────────────────────────────────────────
// support = แจ้งปัญหา/ขอความช่วยเหลือ
// feedback = ข้อเสนอแนะ/คำติชม (พร้อมคะแนน 1-5 ดาว)
// inquiry = สอบถามข้อมูล/ขอคำแนะนำ (Advise / Recommend)
// complaint = ข้อร้องเรียนเร่งด่วน

export const TicketTypeSchema = z.enum(['support', 'feedback', 'inquiry', 'complaint']);
export type TicketType = z.infer<typeof TicketTypeSchema>;

// ─── Ticket Status ───────────────────────────────────────────────────────────
export const TicketStatusSchema = z.enum([
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
]);
export type TicketStatus = z.infer<typeof TicketStatusSchema>;

// ─── Ticket Priority ─────────────────────────────────────────────────────────
export const TicketPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;

// ─── Ticket Channel Source ───────────────────────────────────────────────────
export const TicketSourceSchema = z.enum([
  'web',
  'miniapp',
  'line',
  'whatsapp',
  'wechat',
  'phone',
  'walk_in',
  'manual',
]);
export type TicketSource = z.infer<typeof TicketSourceSchema>;

// ─── Ticket Comment / Timeline Entry ─────────────────────────────────────────
export const TicketCommentTypeSchema = z.enum([
  'message',
  'internal_note',
  'advice_recommendation',
  'resolution',
]);
export type TicketCommentType = z.infer<typeof TicketCommentTypeSchema>;

export const TicketCommentSchema = z.object({
  id: z.string(),
  authorId: z.string().default(''),
  authorName: z.string(),
  authorRole: z.enum(['staff', 'customer', 'system']).default('staff'),
  type: TicketCommentTypeSchema.default('message'),
  content: z.string(),
  createdAt: z.string(),
});
export type TicketComment = z.infer<typeof TicketCommentSchema>;

// ─── Ticket Customer Info ───────────────────────────────────────────────────
export const TicketCustomerInfoSchema = z.object({
  contactId: z.string().optional(),
  name: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  channelUserId: z.string().optional(),
  channel: z.string().optional(),
});
export type TicketCustomerInfo = z.infer<typeof TicketCustomerInfoSchema>;

// ─── Ticket Schema ───────────────────────────────────────────────────────────
export const TicketSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  ticketNumber: z.string(),
  type: TicketTypeSchema.default('support'),
  status: TicketStatusSchema.default('open'),
  priority: TicketPrioritySchema.default('medium'),
  source: TicketSourceSchema.default('web'),
  customer: TicketCustomerInfoSchema,
  subject: z.string(),
  description: z.string(),
  category: z.string().default('general'),
  rating: z.number().min(1).max(5).optional(), // 1-5 ดาวสำหรับ feedback
  assignedTo: z.string().optional(),
  adviceOrRecommendation: z.string().optional(), // คำแนะนำหรือข้อเสนอแนะที่ให้แก่ลูกค้า
  resolution: z.string().optional(), // รายละเอียดการแก้ปัญหา
  comments: z.array(TicketCommentSchema).default([]),
  tags: z.array(z.string()).default([]),
  customFields: z.record(z.any()).default({}), // รองรับ multi-industry fields
  resolvedAt: z.string().optional(),
  closedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Ticket = z.infer<typeof TicketSchema>;

// ─── Create & Update Input Schemas ───────────────────────────────────────────
export const CreateTicketInputSchema = z.object({
  type: TicketTypeSchema.default('support'),
  priority: TicketPrioritySchema.default('medium'),
  source: TicketSourceSchema.default('web'),
  customer: TicketCustomerInfoSchema,
  subject: z.string().min(1, 'Subject is required'),
  description: z.string().min(1, 'Description is required'),
  category: z.string().default('general'),
  rating: z.number().min(1).max(5).optional(),
  assignedTo: z.string().optional(),
  adviceOrRecommendation: z.string().optional(),
  tags: z.array(z.string()).optional(),
  customFields: z.record(z.any()).optional(),
});
export type CreateTicketInput = z.infer<typeof CreateTicketInputSchema>;

export const UpdateTicketInputSchema = z.object({
  status: TicketStatusSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  type: TicketTypeSchema.optional(),
  category: z.string().optional(),
  assignedTo: z.string().optional(),
  adviceOrRecommendation: z.string().optional(),
  resolution: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  tags: z.array(z.string()).optional(),
  customFields: z.record(z.any()).optional(),
});
export type UpdateTicketInput = z.infer<typeof UpdateTicketInputSchema>;

export const AddTicketCommentInputSchema = z.object({
  authorName: z.string().min(1),
  authorRole: z.enum(['staff', 'customer', 'system']).default('staff'),
  type: TicketCommentTypeSchema.default('message'),
  content: z.string().min(1, 'Content is required'),
});
export type AddTicketCommentInput = z.infer<typeof AddTicketCommentInputSchema>;

// ─── Support Stats Summary ───────────────────────────────────────────────────
export interface SupportStats {
  total: number;
  open: number;
  inProgress: number;
  waitingCustomer: number;
  resolved: number;
  closed: number;
  avgRating: number;
  totalRatings: number;
  byType: {
    support: number;
    feedback: number;
    inquiry: number;
    complaint: number;
  };
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
}
